import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { queryKeys } from '@/lib/queryKeys';
import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  isWeekend,
  eachDayOfInterval,
  parseISO,
  format,
} from 'date-fns';
import type { JourDetail } from '@/types/calendar';
import { isAffectedForTeamsPage } from '@/lib/disponibilite-utils';

interface Membre {
  id: string;
  prenom: string;
  nom: string;
  poste: string;
  specialite: string;
  statut: 'sur_chantier' | 'disponible' | 'repos' | 'en_arret' | 'en_rotation';
  taux_horaire?: number;
  coefficient_charges?: number;
  heures_par_jour?: number;
}

export interface CoutBreakdownItem {
  membreId: string;
  nom: string;
  poste: string;
  specialite: string;
  tauxHoraire: number;
  coefficientCharges: number; // ex: 1.85
  heuresParJour: number;
  valueJour: number;
  coutJour: number;
}

interface Affectation {
  id: string;
  membre_equipe_id: string;
  chantier_id: string;
  date_debut: string;
  date_fin: string | null;
  heures_par_jour: number | null;
  taux_horaire_specifique: number | null;
  charges_patronales_pct: number | null;
  jours_details: JourDetail[] | null;
  notes: string | null;
}

interface Suspension {
  id: string;
  chantier_id: string;
  date_suspension: string;
  date_reprise: string | null;
}

export interface EquipeStats {
  totalMembres: number;
  membresDisponibles: number;
  membresSurChantier: number;
  tauxOccupation: number;
  occupationColor: string;
  occupationStatus: 'optimal' | 'attention' | 'critique';
  // Coûts
  coutAujourdhui: number;
  coutBreakdownAujourdhui: CoutBreakdownItem[];
  coutSemaine: number;
  coutMois: number;
  // Mode période
  periodeMode: 'semaine' | 'mois';
  setPeriodeMode: (mode: 'semaine' | 'mois') => void;
  coutPeriode: number;
}

interface UseEquipeStatsParams {
  entrepriseId: string | null;
  membres: Membre[];
}

// Données brutes retournées par la query
interface EquipeStatsRawData {
  affectations: Affectation[];
  suspensions: Suspension[];
}

/**
 * Hook pour calculer les statistiques d'occupation et coûts de l'équipe
 *
 * Migré vers React Query (useQuery + invalidation via events).
 *
 * Calcule :
 * - Nombre de membres disponibles vs total
 * - Taux d'occupation basé sur les affectations actives
 * - Couleur dynamique selon les seuils BTP France (optimal ≥75%)
 * - Coûts journaliers, hebdomadaires et mensuels basés sur jours_details
 *
 * IMPORTANT: Utilise jours_details pour un calcul précis des demi-journées
 * et prend en compte les suspensions de chantier.
 */
export function useEquipeStats({ entrepriseId, membres }: UseEquipeStatsParams): EquipeStats {

  const [periodeMode, setPeriodeMode] = useState<'semaine' | 'mois'>('semaine');

  // -------------------------------------------------------------------------
  // Query : charger affectations + suspensions en parallèle
  // -------------------------------------------------------------------------
  const { data: rawData } = useQuery({
    queryKey: queryKeys.equipe.stats(entrepriseId ?? ''),
    queryFn: async (): Promise<EquipeStatsRawData> => {
      if (import.meta.env.DEV) console.log('[useEquipeStats] Fetching affectations + suspensions...');

      const [affRes, suspRes] = await Promise.all([
        supabase
          .from('affectations_chantiers')
          .select(`
            id, membre_equipe_id, chantier_id, date_debut, date_fin,
            heures_par_jour, taux_horaire_specifique, charges_patronales_pct,
            jours_details, notes,
            chantiers!inner(entreprise_id)
          `)
          .eq('chantiers.entreprise_id', entrepriseId!),
        supabase
          .from('suspensions_chantiers')
          .select(`
            id, chantier_id, date_suspension, date_reprise,
            chantiers!inner(entreprise_id)
          `)
          .eq('chantiers.entreprise_id', entrepriseId!),
      ]);

      if (affRes.error) throw affRes.error;
      if (suspRes.error) throw suspRes.error;

      return {
        affectations: (affRes.data || []) as Affectation[],
        suspensions: (suspRes.data || []) as Suspension[],
      };
    },
    enabled: !!entrepriseId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // -------------------------------------------------------------------------
  // Calcul des statistiques
  // -------------------------------------------------------------------------
  const stats = useMemo(() => {
    const affectations = rawData?.affectations ?? [];
    const suspensions = rawData?.suspensions ?? [];
    const todayStr = new Date().toISOString().split('T')[0];

    // Fonction pour calculer le statut RÉEL basé sur les affectations actives
    // Prend en compte : weekends, jours_details (AM/PM/FULL), suspensions, heure (OFF après 17h)
    const todayDate = new Date();
    const getStatutReel = (membre: Membre): Membre['statut'] => {
      if (membre.statut === 'en_arret') return 'en_arret';

      // Weekend = personne ne travaille
      if (isWeekend(todayDate)) {
        return membre.statut === 'sur_chantier' ? 'disponible' : membre.statut;
      }

      const hasAffectationAujourdhui = affectations.some(a => {
        // Vérifier la plage de dates
        if (a.date_debut > todayStr || (a.date_fin !== null && a.date_fin < todayStr)) {
          return false;
        }
        if (a.membre_equipe_id !== membre.id) return false;
        // Vérifier que le chantier n'est pas suspendu
        if (isDateSuspended(todayStr, a.chantier_id, suspensions)) return false;
        // Vérifier jours_details + période actuelle (AM/PM/OFF après 17h)
        if (!isAffectedForTeamsPage(a.jours_details, todayStr)) return false;
        return true;
      });

      if (hasAffectationAujourdhui) {
        return 'sur_chantier';
      }

      // Si le statut DB dit "sur_chantier" mais aucune affectation active -> disponible
      if (membre.statut === 'sur_chantier') {
        return 'disponible';
      }

      return membre.statut;
    };

    // Total membres actifs (tous sauf en_arret)
    const membresActifs = membres.filter(m => getStatutReel(m) !== 'en_arret');
    const totalMembres = membresActifs.length;

    // Membres disponibles et sur chantier (basé sur statut RÉEL)
    const membresDisponibles = membres.filter(m => getStatutReel(m) === 'disponible').length;
    const membresSurChantier = membres.filter(m => getStatutReel(m) === 'sur_chantier').length;

    // Taux d'occupation
    const tauxOccupation = totalMembres > 0
      ? (membresSurChantier / totalMembres) * 100
      : 0;

    const { color: occupationColor, status: occupationStatus } = getOccupationColor(tauxOccupation);

    // Map des membres pour accès rapide
    const membresMap = new Map(membres.map(m => [m.id, m]));

    // Coût aujourd'hui + breakdown par membre
    const coutAujourdhui = calculerCoutJour(affectations, membresMap, suspensions, todayStr);
    const coutBreakdownAujourdhui = calculerCoutJourBreakdown(affectations, membresMap, suspensions, todayStr);

    // Coût semaine et mois
    const today = new Date();
    const debutSemaine = startOfWeek(today, { weekStartsOn: 1 });
    const finSemaine = endOfWeek(today, { weekStartsOn: 1 });
    const coutSemaine = calculerCoutPeriode(affectations, membresMap, suspensions, debutSemaine, finSemaine);

    const debutMois = startOfMonth(today);
    const finMois = endOfMonth(today);
    const coutMois = calculerCoutPeriode(affectations, membresMap, suspensions, debutMois, finMois);

    return {
      totalMembres,
      membresDisponibles,
      membresSurChantier,
      tauxOccupation,
      occupationColor,
      occupationStatus,
      coutAujourdhui,
      coutBreakdownAujourdhui,
      coutSemaine,
      coutMois,
    };
  }, [rawData, membres]);

  return {
    ...stats,
    periodeMode,
    setPeriodeMode,
    coutPeriode: periodeMode === 'semaine' ? stats.coutSemaine : stats.coutMois,
  };
}

/**
 * Vérifie si une date est dans une période de suspension
 */
function isDateSuspended(
  dateStr: string,
  chantierId: string,
  suspensions: Suspension[]
): boolean {
  return suspensions.some(s =>
    s.chantier_id === chantierId &&
    s.date_suspension <= dateStr &&
    (s.date_reprise === null || s.date_reprise > dateStr)
  );
}

/**
 * Calcule le coût pour un jour donné
 * Utilise jours_details pour les demi-journées
 */
function calculerCoutJour(
  affectations: Affectation[],
  membresMap: Map<string, Membre>,
  suspensions: Suspension[],
  dateStr: string
): number {
  return affectations.reduce((total, a) => {
    // Vérifier que l'affectation couvre cette date
    if (a.date_debut > dateStr || (a.date_fin && a.date_fin < dateStr)) {
      return total;
    }

    // Vérifier si le chantier est suspendu
    if (isDateSuspended(dateStr, a.chantier_id, suspensions)) {
      return total;
    }

    // Récupérer le membre
    const membre = membresMap.get(a.membre_equipe_id);
    if (!membre) return total;

    // Récupérer les taux et charges
    const tauxHoraire = a.taux_horaire_specifique || membre.taux_horaire || 14;
    const coeffCharges = membre.coefficient_charges || 1.85;
    const heuresParJour = a.heures_par_jour || membre.heures_par_jour || 8;

    // Calculer la valeur du jour via jours_details
    let valueJour = 0;

    if (a.jours_details && a.jours_details.length > 0) {
      // Utiliser jours_details pour un calcul précis
      const detailsJour = a.jours_details.filter(j => j.date === dateStr);
      valueJour = detailsJour.reduce((sum, j) => sum + (j.value || 0), 0);
    } else {
      // Fallback: si pas de jours_details, vérifier si c'est un jour ouvré
      const dateObj = parseISO(dateStr);
      if (!isWeekend(dateObj)) {
        valueJour = 1; // Journée complète
      }
    }

    // Si pas de travail ce jour, pas de coût
    if (valueJour === 0) return total;

    // Calcul du coût: taux × heures × value × coefficient_charges
    const coutJour = tauxHoraire * heuresParJour * valueJour * coeffCharges;
    return total + coutJour;
  }, 0);
}

/**
 * Calcule le breakdown du coût jour par membre
 * Même logique que calculerCoutJour mais retourne le détail par membre
 * Agrège les affectations multiples (AM/PM sur différents chantiers)
 */
function calculerCoutJourBreakdown(
  affectations: Affectation[],
  membresMap: Map<string, Membre>,
  suspensions: Suspension[],
  dateStr: string
): CoutBreakdownItem[] {
  const breakdownMap = new Map<string, CoutBreakdownItem>();

  for (const a of affectations) {
    if (a.date_debut > dateStr || (a.date_fin && a.date_fin < dateStr)) continue;
    if (isDateSuspended(dateStr, a.chantier_id, suspensions)) continue;

    const membre = membresMap.get(a.membre_equipe_id);
    if (!membre) continue;

    const tauxHoraire = a.taux_horaire_specifique || membre.taux_horaire || 14;
    const coeffCharges = membre.coefficient_charges || 1.85;
    const heuresParJour = a.heures_par_jour || membre.heures_par_jour || 8;

    let valueJour = 0;
    if (a.jours_details && a.jours_details.length > 0) {
      const detailsJour = a.jours_details.filter(j => j.date === dateStr);
      valueJour = detailsJour.reduce((sum, j) => sum + (j.value || 0), 0);
    } else {
      const dateObj = parseISO(dateStr);
      if (!isWeekend(dateObj)) {
        valueJour = 1;
      }
    }

    if (valueJour === 0) continue;

    const coutAffectation = tauxHoraire * heuresParJour * valueJour * coeffCharges;

    const existing = breakdownMap.get(membre.id);
    if (existing) {
      // Membre avec plusieurs affectations aujourd'hui : agréger
      existing.valueJour += valueJour;
      existing.coutJour += coutAffectation;
    } else {
      breakdownMap.set(membre.id, {
        membreId: membre.id,
        nom: `${membre.prenom} ${membre.nom}`,
        poste: membre.poste || '',
        specialite: membre.specialite || '',
        tauxHoraire,
        coefficientCharges: coeffCharges,
        heuresParJour,
        valueJour,
        coutJour: coutAffectation,
      });
    }
  }

  return Array.from(breakdownMap.values()).sort((a, b) => b.coutJour - a.coutJour);
}

/**
 * Calcule le coût total des affectations sur une période donnée
 *
 * IMPORTANT: Utilise jours_details pour un calcul précis des demi-journées.
 * Prend en compte les suspensions de chantier.
 * Ne compte que les jours ouvrés (lundi-vendredi).
 */
function calculerCoutPeriode(
  affectations: Affectation[],
  membresMap: Map<string, Membre>,
  suspensions: Suspension[],
  debutPeriode: Date,
  finPeriode: Date
): number {
  const debutPeriodeStr = format(debutPeriode, 'yyyy-MM-dd');
  const finPeriodeStr = format(finPeriode, 'yyyy-MM-dd');

  return affectations.reduce((total, a) => {
    const debut = a.date_debut;
    const fin = a.date_fin || '2099-12-31';

    // Vérifier si l'affectation chevauche la période
    if (debut > finPeriodeStr || fin < debutPeriodeStr) return total;

    // Récupérer le membre associé
    const membre = membresMap.get(a.membre_equipe_id);
    if (!membre) return total;

    // Récupérer les taux et charges
    const tauxHoraire = a.taux_horaire_specifique || membre.taux_horaire || 14;
    const coeffCharges = membre.coefficient_charges || 1.85;
    const heuresParJour = a.heures_par_jour || membre.heures_par_jour || 8;

    // Calculer la valeur totale travaillée sur la période
    let totalValue = 0;

    if (a.jours_details && a.jours_details.length > 0) {
      // MÉTHODE PRÉCISE: Utiliser jours_details
      // Filtrer les jours dans la période et non suspendus
      for (const detail of a.jours_details) {
        // Vérifier que le jour est dans la période
        if (detail.date < debutPeriodeStr || detail.date > finPeriodeStr) {
          continue;
        }

        // Vérifier que c'est un jour ouvré
        const dateObj = parseISO(detail.date);
        if (isWeekend(dateObj)) {
          continue;
        }

        // Vérifier que le chantier n'est pas suspendu ce jour
        if (isDateSuspended(detail.date, a.chantier_id, suspensions)) {
          continue;
        }

        // Ajouter la valeur (0.5 pour demi-journée, 1 pour journée complète)
        totalValue += detail.value || 0;
      }
    } else {
      // FALLBACK: Pas de jours_details, compter les jours ouvrés calendaires
      // Borner aux limites de la période et de l'affectation
      const debutEffectif = debut > debutPeriodeStr ? parseISO(debut) : debutPeriode;
      const finEffective = fin < finPeriodeStr ? parseISO(fin) : finPeriode;

      try {
        const joursInterval = eachDayOfInterval({ start: debutEffectif, end: finEffective });

        for (const jour of joursInterval) {
          // Exclure les weekends
          if (isWeekend(jour)) continue;

          // Exclure les jours suspendus
          const jourStr = format(jour, 'yyyy-MM-dd');
          if (isDateSuspended(jourStr, a.chantier_id, suspensions)) continue;

          totalValue += 1; // Journée complète
        }
      } catch {
        totalValue = 0;
      }
    }

    // Si aucun travail sur la période, pas de coût
    if (totalValue === 0) return total;

    // Calcul du coût: taux × heures × totalValue × coefficient_charges
    const coutPeriode = tauxHoraire * heuresParJour * totalValue * coeffCharges;
    return total + coutPeriode;
  }, 0);
}

/**
 * Calcule la couleur selon le taux d'occupation
 *
 * Seuils basés sur données BTP France (seuil recommandé : 80%)
 * - ≥75% = Vert (optimal)
 * - 50-74% = Orange dégradé (attention)
 * - <50% = Rouge dégradé (critique)
 */
function getOccupationColor(percentage: number): { color: string; status: 'optimal' | 'attention' | 'critique' } {
  if (percentage >= 75) {
    return { color: '#22c55e', status: 'optimal' };
  } else if (percentage >= 50) {
    const ratio = (percentage - 50) / 25;
    const r = Math.round(234 + (251 - 234) * ratio);
    const g = Math.round(88 + (146 - 88) * ratio);
    const b = Math.round(12 + (60 - 12) * ratio);
    return { color: `rgb(${r}, ${g}, ${b})`, status: 'attention' };
  } else {
    const ratio = percentage / 50;
    const r = Math.round(153 + (248 - 153) * ratio);
    const g = Math.round(27 + (113 - 27) * ratio);
    const b = Math.round(27 + (113 - 27) * ratio);
    return { color: `rgb(${r}, ${g}, ${b})`, status: 'critique' };
  }
}
